/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Campaigns", description = "Run ongoing lifecycle programs in Lifecycle Studio — the agent drafts a brief, plans a coordinated set of messages, generates candidates you refine and extend over time, and helps you send them to your ESP.", lastUpdatedDate = new Date(1790035200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Campaigns",
        description: "Run ongoing lifecycle programs in Lifecycle Studio — the agent drafts a brief, plans a coordinated set of messages, generates candidates you refine and extend over time, and helps you send them to your ESP.",
        lastUpdatedDate: "Tue Sep 22 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "When to use campaigns",
          "depth": 1,
          "id": "when-to-use-campaigns"
        }, {
          "value": "Create a campaign",
          "depth": 0,
          "id": "create-a-campaign"
        }, {
          "value": "Essentials",
          "depth": 1,
          "id": "essentials"
        }, {
          "value": "Templates",
          "depth": 1,
          "id": "templates"
        }, {
          "value": "Assets",
          "depth": 1,
          "id": "assets"
        }, {
          "value": "Build and launch a campaign",
          "depth": 0,
          "id": "build-and-launch-a-campaign"
        }, {
          "value": "Plan",
          "depth": 1,
          "id": "plan"
        }, {
          "value": "Create",
          "depth": 1,
          "id": "create"
        }, {
          "value": "Launch",
          "depth": 1,
          "id": "launch"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      img: "img",
      h3: "h3",
      ol: "ol",
      li: "li",
      ul: "ul",
      h4: "h4"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Marketers, creative teams"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: _jsxs("ul", {
                children: [_jsx("li", {
                  children: _jsx(_components.a, {
                    href: "/lifecycle-studio/overview#get-started",
                    children: "Lifecycle Studio setup complete"
                  })
                }), _jsxs("li", {
                  children: ["At least one ", _jsx(_components.a, {
                    href: "/lifecycle-studio/templates",
                    children: "template"
                  }), " available"]
                }), _jsxs("li", {
                  children: ["Business context configured in ", _jsx(_components.a, {
                    href: "/context-hub/overview",
                    children: "Context Hub"
                  })]
                })]
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Campaigns are where you run a lifecycle program. Each campaign follows the same path: plan the brief and the messages it will include, generate and refine the content, then send the approved messages to your ESP. You can add messages over time as the program grows."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "A campaign in Lifecycle Studio represents a single lifecycle marketing initiative: a welcome series, a re-engagement flow, a promotional push, or any other goal-driven message sequence. You describe what you want, and the agent drafts a brief, plans the messages it will create, and generates content you refine and send."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/lifecycle-studio/campaigns/ls-campaigns-overview.png",
          alt: "Lifecycle Studio Campaigns list view"
        })
      }), "\n", _jsx(_components.h3, {
        id: "when-to-use-campaigns",
        children: "When to use campaigns"
      }), "\n", _jsxs(_components.p, {
        children: ["Use a campaign to run an ongoing lifecycle program, like a welcome series, a re-engagement flow, or a seasonal push: a coordinated set of messages the agent plans and drafts together. For a single one-off send, use ", _jsx(_components.a, {
          href: "/lifecycle-studio/messages",
          children: "Messages"
        }), "."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "create-a-campaign",
        children: "Create a campaign"
      }), "\n", _jsxs(_components.p, {
        children: ["To create a campaign, go to ", _jsx(_components.strong, {
          children: _jsx(_components.a, {
            href: "https://app.hightouch.com/lifecycle-studio/projects?from=docs",
            children: "Lifecycle Studio > Campaigns"
          })
        }), " and select ", _jsx(_components.strong, {
          children: "Create campaign"
        }), ". The ", _jsx(_components.strong, {
          children: "Campaign context"
        }), " form collects what the agent needs, organized into steps: ", _jsx(_components.strong, {
          children: "Essentials"
        }), ", then ", _jsx(_components.strong, {
          children: "Templates"
        }), " if the campaign includes email, then ", _jsx(_components.strong, {
          children: "Assets"
        }), ". Only ", _jsx(_components.strong, {
          children: "Essentials"
        }), " is required. Select ", _jsx(_components.strong, {
          children: "Next"
        }), " to move through the steps, and ", _jsx(_components.strong, {
          children: "Create campaign"
        }), " on the last step."]
      }), "\n", _jsx(_components.h3, {
        id: "essentials",
        children: "Essentials"
      }), "\n", _jsx(_components.p, {
        children: "Set what the campaign should achieve and who it's for."
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["If the campaign starts from an Asana task, paste the task URL into ", _jsx(_components.strong, {
            children: "Autofill from Asana"
          }), " to pre-fill the form and link the task. See the ", _jsx(_components.a, {
            href: "/lifecycle-studio/asana",
            children: "Asana integration"
          }), "."]
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/lifecycle-studio/campaigns/ls-create-campaign-essentials.png",
          alt: "Essentials step of the Campaign context form"
        })
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Enter a ", _jsx(_components.strong, {
            children: "Campaign name"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select the ", _jsx(_components.strong, {
            children: "parent model"
          }), " for audience data and the ", _jsx(_components.strong, {
            children: "brand profile"
          }), " the agent should follow."]
        }), "\n", _jsxs(_components.li, {
          children: ["Choose the ", _jsx(_components.strong, {
            children: "channels"
          }), " to include: ", _jsx(_components.strong, {
            children: "Email"
          }), ", ", _jsx(_components.strong, {
            children: "Push"
          }), ", or ", _jsx(_components.strong, {
            children: "SMS"
          }), ". Including ", _jsx(_components.strong, {
            children: "Email"
          }), " adds a ", _jsx(_components.strong, {
            children: "Templates"
          }), " step."]
        }), "\n", _jsxs(_components.li, {
          children: ["Describe your ", _jsx(_components.strong, {
            children: "Goal"
          }), " — what you want the campaign to achieve."]
        }), "\n", _jsxs(_components.li, {
          children: ["Choose the ", _jsx(_components.strong, {
            children: "target audience"
          }), ":", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "Single audience"
              }), " — One audience for the whole campaign. Optionally describe who they are and where they are in their journey."]
            }), "\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "Multiple sub-audiences"
              }), " — A different segment for each message. The agent identifies the segments and plans messages for each."]
            }), "\n"]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "templates",
        children: "Templates"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "Templates"
        }), " step appears when the campaign includes email, and it's optional."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/lifecycle-studio/campaigns/ls-create-campaign-templates.png",
          alt: "Templates step of the Campaign context form, with template selection and candidate quantity"
        })
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Select up to 10 ", _jsx(_components.a, {
            href: "/lifecycle-studio/templates",
            children: "email templates"
          }), " for the agent to start from."]
        }), "\n", _jsxs(_components.li, {
          children: ["Choose how the agent uses them. ", _jsx(_components.strong, {
            children: "Adhere directly"
          }), " keeps the layout and updates only imagery and copy. ", _jsx(_components.strong, {
            children: "Use as inspiration"
          }), " restructures into a new layout. Add notes to guide the agent if needed."]
        }), "\n", _jsxs(_components.li, {
          children: ["Set the ", _jsx(_components.strong, {
            children: "Candidate quantity"
          }), ", the number of variations the agent generates per message."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "assets",
        children: "Assets"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "Assets"
        }), " step is optional. Use it to set personalization and give the agent anything else it should know."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/lifecycle-studio/campaigns/ls-create-campaign-assets.png",
          alt: "Assets step of the Campaign context form, with personalization, products and images, and plan review"
        })
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Set ", _jsx(_components.strong, {
            children: "Personalization"
          }), " to ", _jsx(_components.strong, {
            children: "Allow"
          }), " so the agent can tailor content per recipient, or ", _jsx(_components.strong, {
            children: "Don't allow"
          }), " to send everyone the same content. When you allow it, describe the personalization you want."]
        }), "\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "Products & images"
          }), ", select ", _jsx(_components.strong, {
            children: "Add products or images"
          }), " to give the agent catalog items or images to reference."]
        }), "\n", _jsxs(_components.li, {
          children: ["In ", _jsx(_components.strong, {
            children: "Additional context"
          }), ", describe how the agent should use the assets, upload files, paste reference links, or add notes such as promotions, copy to use or avoid, and tone."]
        }), "\n", _jsxs(_components.li, {
          children: ["Leave ", _jsx(_components.strong, {
            children: "Plan review"
          }), " on to review the brief and message plan before the agent generates candidates. Turn it off to generate the brief, plan, and candidates in one pass."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["If the campaign doesn't include email, set the ", _jsx(_components.strong, {
          children: "Candidate quantity"
        }), " in this step instead."]
      }), "\n", _jsxs(_components.p, {
        children: ["Select ", _jsx(_components.strong, {
          children: "Create campaign"
        }), ". The agent generates a brief and a message plan based on your inputs and the business context, brand guidelines, and guardrails configured in ", _jsx(_components.a, {
          href: "/context-hub/overview",
          children: "Context Hub"
        }), "."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "build-and-launch-a-campaign",
        children: "Build and launch a campaign"
      }), "\n", _jsxs(_components.p, {
        children: ["After you create a campaign, it opens with three tabs: ", _jsx(_components.strong, {
          children: "Plan"
        }), ", ", _jsx(_components.strong, {
          children: "Create"
        }), ", and ", _jsx(_components.strong, {
          children: "Launch"
        }), ". Work through them in order to take the campaign from brief to delivery."]
      }), "\n", _jsx(_components.h3, {
        id: "plan",
        children: "Plan"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/lifecycle-studio/campaigns/ls-campaigns-plan.png",
          alt: "Campaign Plan tab showing the brief and message plan checklist"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["After you create the campaign, review and refine the brief in the ", _jsx(_components.strong, {
          children: "Plan"
        }), " tab. The brief captures the campaign goal, target audience, messaging direction, and constraints, and it's the foundation for everything the agent generates next."]
      }), "\n", _jsxs(_components.p, {
        children: ["You can refine the brief in two ways: give the agent direction in the chat, or select ", _jsx(_components.strong, {
          children: "Edit"
        }), " to open the brief editor and change it directly. In the editor, select ", _jsx(_components.strong, {
          children: "Update"
        }), " to save. When you save, you choose how to handle the message plan:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Just save the brief"
          }), " — Save your edits without changing any existing message plans."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Update message plans"
          }), " — Save your edits and have the agent revise draft message plans to match the updated brief. Plans you've already generated candidates from aren't changed."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/lifecycle-studio/campaigns/ls-campaigns-update-message-plans.png",
          alt: "Update message plans dialog with Update message plans and Just save the brief options"
        })
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "The brief incorporates your workspace context automatically. If there's context specific to a campaign, like a seasonal constraint, a specific product focus, or a competitive angle, add it to your brief via the agent chat rather than starting over."
        })
      }), "\n", _jsxs(_components.p, {
        children: ["When the brief and message plan look right, open the ", _jsx(_components.strong, {
          children: "Create"
        }), " tab to generate candidates."]
      }), "\n", _jsx(_components.h3, {
        id: "create",
        children: "Create"
      }), "\n", _jsxs(_components.p, {
        children: ["In ", _jsx(_components.strong, {
          children: "Create"
        }), ", the plan becomes real drafts. The agent generates ", _jsx(_components.strong, {
          children: "candidates"
        }), ": several variations of each message in the plan. You review them and choose which ones to carry forward."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/lifecycle-studio/campaigns/ls-campaigns-create.png",
          alt: "Candidates the agent generated for each planned message"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Select ", _jsx(_components.strong, {
          children: "Generate all candidates"
        }), " to draft variations for every message in the plan at once, or work a single message with ", _jsx(_components.strong, {
          children: "Generate more"
        }), ". If the brief targets multiple audience segments, the agent generates a separate set of messages for each segment, so the campaign can speak differently to each one."]
      }), "\n", _jsx(_components.p, {
        children: "From there, the work is comparing and narrowing:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Review the variations for each message and ", _jsx(_components.strong, {
            children: "favorite"
          }), " the ones you like."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select a candidate and tell the agent what to change in the chat, or open it to edit in the ", _jsx(_components.a, {
            href: "/lifecycle-studio/messages#iterate-with-the-agent-chat",
            children: "message editor"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["If nothing in a set lands, select ", _jsx(_components.strong, {
            children: "Generate more"
          }), " for a fresh batch of that message."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["A candidate stays a draft until you promote it. Select the candidate and choose ", _jsx(_components.strong, {
          children: "Promote to Launch"
        }), " to move it to the ", _jsx(_components.strong, {
          children: "Launch"
        }), " tab."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/lifecycle-studio/campaigns/ls-campaigns-promote.png",
          alt: "Create tab with a candidate selected and the Promote to Launch button"
        })
      }), "\n", _jsx(_components.h4, {
        id: "adjust-how-the-agent-approaches-a-message",
        children: "Adjust how the agent approaches a message"
      }), "\n", _jsxs(_components.p, {
        children: ["Each candidate is generated from a ", _jsx(_components.strong, {
          children: "message plan"
        }), ": the agent's intended action, audience, and creative direction for that message. To change how the agent approaches a message, edit its plan."]
      }), "\n", _jsxs(_components.p, {
        children: ["Select ", _jsx(_components.strong, {
          children: "Message plan"
        }), " for the message you want to change. You can revise three fields:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Desired action"
          }), " — what you want the recipient to do."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Creative guidance"
          }), " — the angle, tone, and constraints for the agent to follow."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Recipient"
          }), " — who the message is for."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Save your changes, then generate again. The agent uses the updated plan for that message's candidates."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/lifecycle-studio/campaigns/ls-campaigns-edit-message-plan.png",
          alt: "Editing a message plan's desired action, creative guidance, and recipient"
        })
      }), "\n", _jsx(_components.h4, {
        id: "add-a-message-to-the-campaign",
        children: "Add a message to the campaign"
      }), "\n", _jsxs(_components.p, {
        children: ["To add a message the plan didn't include, select ", _jsx(_components.strong, {
          children: "Add messages"
        }), " and describe what you want. The agent updates the campaign brief and plans the new messages alongside the existing ones. Select channels if you want specific ones. Otherwise, the agent reuses the channels already in the brief."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/lifecycle-studio/campaigns/ls-campaigns-add-messages.png",
          alt: "Add messages dialog with description, channels, candidate quantity, and template options"
        })
      }), "\n", _jsx(_components.h3, {
        id: "launch",
        children: "Launch"
      }), "\n", _jsxs(_components.p, {
        children: ["Promoted candidates appear in the ", _jsx(_components.strong, {
          children: "Launch"
        }), " tab. For each one, pair it with an audience, sync that audience to your destination, and export the message to your ESP."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/lifecycle-studio/campaigns/ls-campaigns-launch.png",
          alt: "Campaign Launch tab showing promoted candidates ready to pair with audiences"
        })
      }), "\n", _jsx(_components.h4, {
        id: "pair-each-candidate-with-an-audience",
        children: "Pair each candidate with an audience"
      }), "\n", _jsx(_components.p, {
        children: "Decide who receives each candidate. Audiences come from Customer Studio. The agent suggests an audience based on the message plan, or you can select an existing one or create a new one. Review each audience's size to check reach before you export."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/lifecycle-studio/campaigns/ls-campaigns-add-audience.png",
          alt: "Add audience dialog with suggested audience and select dropdown"
        })
      }), "\n", _jsx(_components.h4, {
        id: "sync-the-audience-to-your-destination",
        children: "Sync the audience to your destination"
      }), "\n", _jsx(_components.p, {
        children: "Once a candidate has an audience, set up a sync to deliver the audience data:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Add sync"
          }), " for the candidate."]
        }), "\n", _jsx(_components.li, {
          children: "Select a sync template, or create a new sync to send the audience to your destination."
        }), "\n", _jsx(_components.li, {
          children: "Review the sync configuration."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/lifecycle-studio/campaigns/ls-campaigns-add-sync.png",
          alt: "Create syncs from templates dialog with ESP sync options"
        })
      }), "\n", _jsx(_components.h4, {
        id: "export-the-message-content",
        children: "Export the message content"
      }), "\n", _jsx(_components.p, {
        children: "The sync moves the audience. Separately, export the message content itself:"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/lifecycle-studio/campaigns/ls-campaigns-send-export.png",
          alt: "Export menu with email provider, HTML, and PDF options"
        })
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Action"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Export to email provider"
              })
            }), _jsx(_components.td, {
              children: "Create a new HTML template in your connected ESP, or update an existing one."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Download HTML"
              })
            }), _jsx(_components.td, {
              children: "Download the message as a raw HTML file."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Download PDF"
              })
            }), _jsx(_components.td, {
              children: "Download a PDF rendering of the message."
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "When you export to an email provider, choose the destination, then create a new template or update an existing one. Set the template name, subject line, and preheader."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/lifecycle-studio/campaigns/ls-campaigns-export-template.png",
          alt: "Export HTML template dialog with destination, create-or-update mode, name, subject, and preheader"
        })
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Exporting to your ESP creates or updates a template there. The actual send happens in your ESP, where you manage timing, frequency capping, and delivery rules."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/lifecycle-studio/messages",
            children: "Messages"
          }), " — Create and edit individual messages"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/lifecycle-studio/templates",
            children: "Templates"
          }), " — Manage structural starting points for messages"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/lifecycle-studio/images",
            children: "Images"
          }), " — Manage the image library the agent draws from"]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
